import React, { useEffect, useRef, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  Box,
  Stack,
  Typography,
  Button,
  LinearProgress,
  Divider,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api, { streamParseTranscript } from "./api";
import Diagram from "./Diagram";
import theme from "./theme";
import "./App.css";

export default function App() {
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [steps, setSteps] = useState([]);
  const [tags, setTags] = useState([]);
  const [turns, setTurns] = useState([]);
  const [fallbackPlan, setFallbackPlan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [sampleMenuAnchor, setSampleMenuAnchor] = useState(null);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const timeoutsRef = useRef([]);
  const contentRef = useRef(null);
  const [diagramKey, setDiagramKey] = useState(0);

  // Sample files available for demo
  const sampleFiles = [
    {
      name: "Care Planning Goals",
      filename: "careplanning-transcript.txt",
      description: "Team debate on care plan goal setting using SMART framework"
    },
    {
      name: "CCM Call Workflow",
      filename: "ccm-transcript.txt", 
      description: "Monthly care calls workflow discussion with compliance issues"
    },
    {
      name: "Critical Alerts",
      filename: "critical-alerts.txt",
      description: "RPM critical alert decision tree and escalation protocols"
    },
    {
      name: "Duplicate Orders",
      filename: "duplicates-transcript.txt",
      description: "Device order duplication protocol meeting"
    },
    {
      name: "Eligibility Criteria",
      filename: "eligibility-transcript.txt",
      description: "RPM, CCM, and BHI program eligibility discussion"
    },
    {
      name: "RPM Call Workflow",
      filename: "rpm-transcript.txt",
      description: "Remote patient monitoring call workflow refinement"
    }
  ];

  const clearPendingTurns = () => {
    timeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutsRef.current = [];
  };

  useEffect(() => () => clearPendingTurns(), []);

  // Content scroll handler removed; we'll rely on header backdrop blur only.

  // streaming adds turns in real-time; keep helper for potential non-streaming paths
  const streamTurns = (turnArray) => {
    clearPendingTurns();
    if (!Array.isArray(turnArray) || turnArray.length === 0) {
      setTurns([]);
      return;
    }
    setTurns((prev) => [...prev, ...turnArray]);
  };

  const handleFileChange = (event) => {
    clearPendingTurns();
    const selected = event.target.files && event.target.files[0];
    setFile(selected || null);
    setError("");
    setTurns([]);
    setSteps([]);
    setTags([]);
    setFallbackPlan([]);

    // Read file content for accordion display
    if (selected) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFileContent(e.target.result);
      };
      reader.readAsText(selected);
    } else {
      setFileContent("");
    }
  };

  const handleSampleFileSelect = async (sampleFile) => {
    clearPendingTurns();
    setSampleMenuAnchor(null);
    setError("");
    setTurns([]);
    setSteps([]);
    setTags([]);
    setFallbackPlan([]);

    try {
      const response = await fetch(`/samples/${sampleFile.filename}`);
      const text = await response.text();
      
      // Store content for accordion display
      setFileContent(text);
      
      // Create a File-like object from the sample text
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], sampleFile.filename, { type: 'text/plain' });
      
      setFile(file);
    } catch (err) {
      setError(`Failed to load sample file: ${err.message}`);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError("Select a transcript file first.");
      return;
    }

    setLoading(true);
    setError("");
    setTurns([]);
    setSteps([]);
    setTags([]);
    setFallbackPlan([]);

    try {
      await streamParseTranscript(file, {
        onTurn: (turn) => setTurns((prev) => [...prev, turn]),
        onFinal: (final) => {
          setSteps(Array.isArray(final?.protocol?.steps) ? final.protocol.steps : []);
          setTags(Array.isArray(final?.tags) ? final.tags : []);
          setFallbackPlan(
            Array.isArray(final?.fallback_plan) ? final.fallback_plan : []
          );
          setLoading(false);
        },
        onError: (e) => {
          setError(e.message);
          setLoading(false);
        },
      });
    } catch (err) {
      setError(err.message || "Stream failed.");
      setLoading(false);
    }
  };

  const resolveTagLabel = (tag) => {
    if (typeof tag === "string") return tag;
    if (tag?.label) return tag.label;
    if (Array.isArray(tag?.labels) && tag.labels.length > 0) return tag.labels[0];
    if (tag?.sequence) return tag.sequence;
    return "Tag";
  };

  const handleReset = () => {
    clearPendingTurns();
    setFile(null);
    setFileContent("");
    setSteps([]);
    setTags([]);
    setTurns([]);
    setFallbackPlan([]);
    setError("");
    setTranscriptExpanded(false);
    setFileInputKey((prev) => prev + 1);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(140deg, rgba(18,16,42,1) 0%, rgba(9,13,32,1) 45%, rgba(27,20,68,1) 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(60% 80% at 20% 20%, rgba(123,97,255,0.4), transparent), radial-gradient(50% 70% at 80% 10%, rgba(97,218,251,0.35), transparent)",
            filter: "blur(120px)",
            opacity: 0.75
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, rgba(10,12,32,0.85) 0%, rgba(10,12,32,0) 30%, rgba(10,12,32,0) 70%, rgba(22,18,55,0.9) 100%)",
            pointerEvents: "none"
          }}
        />
        


        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...(theme.custom?.glassStrong || {}),
            borderRadius: 0,
          }}
        >
          {/* Scroll container holds header (sticky) and content so backdrop-filter can sample children */}
          <Box
            ref={contentRef}
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              background: "rgba(18,16,42,0.18)",
              borderRadius: 0,
              // Space for fixed footer at bottom
              pb: { xs: 10, md: 12 },
            }}
          >
            {/* Sticky header with internal pseudo-element backdrop for reliable blur */}
            <Box
              component="header"
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                px: { xs: 3, md: 5 },
                pt: { xs: 3, md: 5 },
                pb: { xs: 2, md: 3 },
                transition: "all 220ms ease",
                // Layer for tint; the blur is applied via ::before to avoid browser quirks
                background: "rgba(16,18,48,0.45)",
                borderBottom: "1px solid rgba(123,97,255,0.22)",
                boxShadow: "0 18px 42px rgba(6,8,30,0.32)",
                // Pseudo-element does the backdrop filtering and stays behind header content
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backdropFilter: 'blur(18px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                  background: 'rgba(16,18,48,0.45)',
                  zIndex: -1,
                  pointerEvents: 'none',
                },
                // Ensure children render above the pseudo-element
                '& > *': { position: 'relative', zIndex: 1 },
                minHeight: transcriptExpanded ? { xs: '300px', md: '350px' } : 'auto',
              }}
            >
            <Stack spacing={1}>
              <Typography variant="overline" color="primary.light" letterSpacing={2}>
                collaborative agents
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                Echo Chamber
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Upload a transcript to watch Gemini, OpenAI, and Anthropic debate the flow
                before rendering it as an interactive diagram.
              </Typography>
            </Stack>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <input
                    key={fileInputKey}
                    accept=".txt,.md,.pdf,.doc,.docx"
                    id="transcript-file"
                    type="file"
                    hidden
                    onChange={handleFileChange}
                  />
                  <label htmlFor="transcript-file">
                    <Button
                      component="span"
                      size="large"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      sx={{ px: 3.5 }}
                    >
                      Choose File
                    </Button>
                  </label>
                  <Button
                    size="large"
                    variant="text"
                    endIcon={<ExpandMoreIcon />}
                    onClick={(event) => setSampleMenuAnchor(event.currentTarget)}
                    sx={{ px: 3.5, minWidth: 'auto' }}
                  >
                    Try Sample
                  </Button>
                  <Menu
                    anchorEl={sampleMenuAnchor}
                    open={Boolean(sampleMenuAnchor)}
                    onClose={() => setSampleMenuAnchor(null)}
                    sx={{
                      '& .MuiPaper-root': {
                        ...theme.custom?.glassSoft,
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(123,97,255,0.3)',
                        maxHeight: '60vh',
                        width: '320px',
                      }
                    }}
                  >
                    <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, fontSize: '0.75rem' }}>
                      <Typography variant="overline" color="primary.light" letterSpacing={1}>
                        Healthcare Workflow Transcripts
                      </Typography>
                    </MenuItem>
                    {sampleFiles.map((sample) => (
                      <MenuItem
                        key={sample.filename}
                        onClick={() => handleSampleFileSelect(sample)}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 1.5,
                          px: 2,
                          minWidth: 280,
                          '&:hover': {
                            backgroundColor: 'rgba(123,97,255,0.1)',
                          }
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {sample.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                          {sample.description}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      flexGrow: 1,
                      textAlign: { xs: "center", md: "left" },
                      px: { xs: 0, md: 2 }
                    }}
                  >
                    {file ? file.name : "No file selected"}
                  </Typography>
                  <Button
                    type="submit"
                    size="large"
                    variant="outlined"
                    disabled={loading}
                    sx={{ px: 3.5 }}
                  >
                    Generate Diagram
                  </Button>
                </Stack>

                {loading ? <LinearProgress color="primary" /> : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
              </Stack>

              {file && fileContent && (
                <Box sx={{ mt: 3 }}>
                  <Accordion 
                    expanded={transcriptExpanded} 
                    onChange={(event, isExpanded) => setTranscriptExpanded(isExpanded)}
                    sx={{
                      borderRadius: 3,
                      ...(theme.custom?.glassSoft || {}),
                      border: "1px solid rgba(123,97,255,0.3)",
                      boxShadow: "0 8px 32px rgba(6,7,25,0.25)",
                      '&:before': {
                        display: 'none',
                      },
                      '& .MuiAccordionSummary-root': {
                        borderRadius: transcriptExpanded ? '12px 12px 0 0' : '12px',
                        transition: 'border-radius 0.2s ease',
                      },
                      '& .MuiAccordionDetails-root': {
                        borderRadius: '0 0 12px 12px',
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(123,97,255,0.8)' }} />}
                      sx={{
                        backgroundColor: 'rgba(123,97,255,0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(123,97,255,0.12)',
                        },
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 2,
                        }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.light' }}>
                        View Transcript: {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fileContent.length.toLocaleString()} characters
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: 'rgba(16,18,48,0.4)',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        p: 2,
                      }}
                    >
                      <Typography
                        component="pre"
                        variant="body2"
                        sx={{
                          m: 0,
                          whiteSpace: "pre-wrap",
                          fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
                          fontSize: '0.8rem',
                          lineHeight: 1.5,
                          color: 'rgba(255,255,255,0.85)',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {fileContent}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>
            {/* Close header */}
            </Box>

            {/* Main content area */}
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 3, md: 5 },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >


            {turns.length > 0 ? (
              <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 6 }}>
                <Divider textAlign="left" sx={{ borderColor: "rgba(255,255,255,0.12)" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Agent Chain
                  </Typography>
                </Divider>
                <Stack spacing={1.6}>
                  {turns.map((turn, index) => (
                    <Box
                      key={turn?.id || index}
                      sx={{
                        borderRadius: 3,
                        ...(theme.custom?.glassSoft || {}),
                        p: { xs: 2, md: 3 },
                        boxShadow: "0 14px 48px rgba(6,7,25,0.42)",
                        border: "1px solid rgba(123,97,255,0.35)",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.2}
                        spacing={1.2}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={turn?.speaker || "Agent"}
                            sx={{
                              borderColor: "rgba(123,97,255,0.6)",
                              background: "rgba(123,97,255,0.08)",
                              color: "rgba(235,238,255,0.94)",
                              fontWeight: 600,
                              letterSpacing: 0.4,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ letterSpacing: 0.6 }}
                          >
                            step #{turn?.id || index + 1}
                          </Typography>
                        </Stack>
                        <Chip
                          size="small"
                          label={turn?.model || "agent"}
                          sx={{
                            borderColor: "rgba(97,218,251,0.55)",
                            background: "rgba(97,218,251,0.08)",
                            color: "rgba(218,244,255,0.92)",
                            fontWeight: 500,
                          }}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography
                        component="pre"
                        sx={{
                          m: 0,
                          whiteSpace: "pre-wrap",
                          fontFamily: '"Roboto Condensed", "Inter", "Segoe UI", sans-serif',
                          fontWeight: 300,
                          lineHeight: 1.65
                        }}
                      >
                        {turn?.content || "No content"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {tags.length > 0 ? (
              <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 6 }}>
                <Divider textAlign="center" sx={{ borderColor: "rgba(255,255,255,0.12)" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tags
                  </Typography>
                </Divider>
                <Stack
                  direction="row"
                  spacing={1.2}
                  flexWrap="wrap"
                  rowGap={1.6}
                  justifyContent="center"
                >
                  {tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={resolveTagLabel(tag)}
                      variant="outlined"
                      sx={{
                        borderColor: "rgba(123,97,255,0.4)",
                        backgroundColor: "rgba(123,97,255,0.16)",
                        color: "rgba(230,232,255,0.92)",
                        fontWeight: 500
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {fallbackPlan.length > 0 ? (
              <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 6 }}>
                <Divider textAlign="left" sx={{ borderColor: "rgba(255,255,255,0.12)" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Action Plan
                  </Typography>
                </Divider>
                <Stack spacing={1.25}>
                  {fallbackPlan.map((item, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "rgba(123,97,255,0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "primary.contrastText"
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.7 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : null}

            {steps.length > 0 ? (
              <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 6 }}>
                <Divider textAlign="left" sx={{ borderColor: "rgba(255,255,255,0.12)" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Protocol Diagram
                  </Typography>
                </Divider>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: -1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDiagramKey((k) => k + 1)}
                    sx={{
                      borderColor: 'rgba(123,97,255,0.35)',
                      color: 'rgba(230,232,255,0.9)'
                    }}
                  >
                    Re-render Diagram
                  </Button>
                </Stack>
                <Box
                  sx={{
                    // Make the diagram area larger and responsive
                    height: { xs: 520, md: 680, lg: 760 },
                    borderRadius: 3,
                    overflow: "hidden",
                    ...(theme.custom?.glass || {}),
                  }}
                >
                  <Diagram key={diagramKey} refreshKey={diagramKey} steps={steps} />
                </Box>
              </Stack>
            ) : null}
            </Box>
          </Box>

          <Box
            component="footer"
            sx={{
              px: { xs: 3, md: 5 },
              py: { xs: 2, md: 3 },
              position: "fixed",
              left: 0,
              bottom: 0,
              width: "100vw",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              zIndex: 10,
              background: "rgba(10,12,36,0.55)",
              borderTop: "1px solid rgba(123,97,255,0.22)",
              backdropFilter: "blur(16px) saturate(135%)",
              WebkitBackdropFilter: "blur(16px) saturate(135%)",
              boxShadow: "0 -16px 42px rgba(6,8,30,0.22)",
              transition: "all 220ms ease",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {turns.length > 0
                ? `${turns.length} agent messages • Diagram ${steps.length > 0 ? "ready" : "pending"}`
                : file && fileContent 
                ? `Transcript loaded (${fileContent.length.toLocaleString()} chars) • Ready to process`
                : "Upload a transcript to start the chain."}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {file ? file.name : "No file selected"}
              </Typography>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={handleReset}
                sx={{ opacity: 0.8 }}
              >
                Reset
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
